import { useEffect, useState } from 'react';
import { resolveReferences } from '../data/referenceDefaults';
import { previewServiceProperty } from '../data/wizard';
import type { PropertyPreviewResult } from '../data/types';

/** Debounced/cached the same way RecordPicker debounces its search — an unchanged resolved
 * input never re-fires the call, which matters when the bound service is a paid AI prompt. */
const DEBOUNCE_MS = 250;

export type ServicePreviewStatus = 'idle' | 'waiting' | 'pending' | 'ready' | 'error';

export interface ServicePropertyPreviewState {
  status: ServicePreviewStatus;
  result: PropertyPreviewResult | null;
}

function parseServiceBinding(configJson: string): { serviceId: string; inputValues: Record<string, string> } {
  try {
    const parsed = JSON.parse(configJson || '{}') as { serviceId?: unknown; inputValues?: unknown };
    const rawInputValues =
      parsed.inputValues && typeof parsed.inputValues === 'object' && !Array.isArray(parsed.inputValues)
        ? (parsed.inputValues as Record<string, unknown>)
        : {};
    const inputValues = Object.fromEntries(
      Object.entries(rawInputValues).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    );
    return { serviceId: typeof parsed.serviceId === 'string' ? parsed.serviceId : '', inputValues };
  } catch {
    return { serviceId: '', inputValues: {} };
  }
}

/**
 * Live-calls the service a `source: service` property is bound to, as soon as every explicitly
 * mapped `inputValues` expression resolves to something non-empty (no mapping at all -> nothing
 * to wait for, calls immediately). Mirrors ServiceBinding.ResolveInputValues client-side just to
 * decide *readiness*; the actual resolution (including matching-name fallback for anything left
 * unmapped) still happens server-side against the real current values, so a preview can never
 * disagree with what generation would produce.
 */
export function useServicePropertyPreview(
  templateId: string | undefined,
  tokenName: string,
  configJson: string,
  values: Record<string, string>
): ServicePropertyPreviewState {
  const { serviceId, inputValues } = parseServiceBinding(configJson);
  const mappings = Object.entries(inputValues);
  const resolved = Object.fromEntries(mappings.map(([name, expression]) => [name, resolveReferences(expression, values)]));
  const ready = serviceId !== '' && mappings.every(([name]) => resolved[name].trim() !== '');
  const key = ready ? JSON.stringify(resolved) : '';

  const [settled, setSettled] = useState<ReadonlyMap<string, PropertyPreviewResult>>(new Map());
  const [failedKey, setFailedKey] = useState('');

  useEffect(() => {
    if (!templateId || key === '' || settled.has(key)) return;

    const timer = setTimeout(() => {
      previewServiceProperty(templateId, tokenName, values)
        .then((result) => setSettled((current) => new Map(current).set(key, result)))
        .catch(() => setFailedKey(key));
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [templateId, tokenName, key, values, settled]);

  if (serviceId === '') return { status: 'idle', result: null };
  if (!ready) return { status: 'waiting', result: null };
  if (failedKey === key) return { status: 'error', result: null };

  const result = settled.get(key);
  return result ? { status: 'ready', result } : { status: 'pending', result: null };
}
